/**
 * What a project's repository scans add up to.
 *
 * The project list card used to show a name, the slug (which is the name
 * lower-cased), a repository count that was 1 on every row, and a relative
 * scan time. Eleven cards, and nothing on them told the operator anything the
 * name had not already said.
 *
 * Meanwhile `GET /projects` returns, for every repository, the full scan the
 * node performed: which host holds the checkout, the branch, the head commit
 * with its subject and author, whether the tree is dirty, the detected stack
 * and an inventory of the agent-configuration files. All of it was on the wire
 * and none of it was drawn. These functions turn those scans into the handful
 * of facts an operator reads a project by, so the card and the overview agree
 * about them and so the derivation can be tested without a browser.
 *
 * Nothing here invents a reading. A scan that never ran, a path that is
 * missing, and a tree whose cleanliness could not be determined are three
 * different answers and stay three different answers — the interface must not
 * render any of them as "fine".
 */

import type { Project, Repository, RepositoryScan } from '@/types/domain'

/**
 * An agent a checkout is configured for.
 *
 * Read from the same evidence `internal/repoinspect` reads: `CLAUDE.md` means
 * claude-configured, a `.codex` directory means codex-configured, `.kimene`
 * means Kimene's own project file is present. The scan already publishes these
 * three in `stack`, so this reads them from there rather than re-deriving them
 * from the inventory and risking a different answer.
 */
export type AgentConfig = 'claude' | 'codex' | 'kimene'

const AGENT_MARK: Record<string, AgentConfig> = {
  'claude-configured': 'claude',
  'codex-configured': 'codex',
  'kimene-configured': 'kimene',
}

/**
 * A credential override found in a checkout, with the checkout that holds it.
 *
 * The repository name travels with it because the finding is about a *place*:
 * "this project overrides the credential" is not actionable, and "the web
 * checkout's .claude/settings.local.json sets apiKeyHelper" is.
 */
export interface ProjectCredentialOverride {
  repository: string
  path: string
  setting: string
}

/** How a single checkout reads. Each field is a fact or an absence, never a
 *  guess: `unread` is not `broken`, and `dirtyUnknown` is not `clean`. */
export interface CheckoutReading {
  /** No scan has ever run against this repository. */
  unread: boolean
  /** The scan ran and could not use the checkout: the path is gone, git could
   *  not run, or the scan itself failed. */
  broken: boolean
  dirty: boolean
  /** The scan ran but could not determine whether the tree is dirty. */
  dirtyUnknown: boolean
  branch?: string
  hostId?: string
  /** How many files the scan found uncommitted. Absent when the scan reported
   *  a dirty tree without counting — "there is uncommitted work" and "there are
   *  five uncommitted files" are different claims and only one of them is
   *  always available. */
  dirtyFiles?: number
}

export function readCheckout(repo: Repository): CheckoutReading {
  const scan: RepositoryScan | undefined = repo.scan
  if (!scan) {
    return { unread: true, broken: false, dirty: false, dirtyUnknown: true, hostId: repo.hostId }
  }
  const broken = Boolean(scan.error) || !scan.pathExists || !scan.gitAvailable
  return {
    unread: false,
    broken,
    dirty: scan.dirty === true,
    // Unknown when the scan could not read it, and also whenever the checkout
    // is broken — a tree Kimene cannot open is not a clean one.
    dirtyUnknown: scan.dirty == null || broken,
    branch: scan.branch,
    hostId: repo.hostId,
    dirtyFiles: scan.dirty === true ? scan.dirtyFileCount : undefined,
  }
}

/** Everything the list card and the overview read a project by. */
export interface ProjectReading {
  repositories: number
  /** Distinct host ids holding a checkout, in first-seen order. */
  hosts: string[]
  /** Distinct branches, in first-seen order. Two entries mean the project's
   *  checkouts disagree, which is worth seeing on the card. */
  branches: string[]
  /** Technologies, without the three `*-configured` marks — those are the
   *  agent configuration and are reported separately, because "this tree is a
   *  Go project" and "an agent can be told what to do here" are different
   *  questions. */
  stack: string[]
  agents: AgentConfig[]
  dirty: number
  /** Uncommitted files across the dirty checkouts, when the scan counted them.
   *  Absent rather than zero when it did not: `dirty` already says there is
   *  work, and a "0" beside it would say the opposite. */
  dirtyFiles?: number
  dirtyUnknown: number
  unread: number
  broken: number
  /** Settings in the checkouts' own provider configuration that replace the
   *  account a session was routed to. Empty is the ordinary state; one entry
   *  means every session opened in that directory runs on something other than
   *  the credential Kimene chose, whatever the console says it chose. */
  credentialOverrides: ProjectCredentialOverride[]
  /** The newest head commit across the checkouts, for "what happened here
   *  last". Absent when no scan carries one. */
  head?: { at?: string; subject?: string; sha?: string; author?: string }
}

export function readProject(project: Project): ProjectReading {
  const repos = project.repositories ?? []
  const out: ProjectReading = {
    repositories: repos.length,
    hosts: [],
    branches: [],
    stack: [],
    agents: [],
    dirty: 0,
    dirtyUnknown: 0,
    unread: 0,
    broken: 0,
    credentialOverrides: [],
  }

  for (const repo of repos) {
    const r = readCheckout(repo)
    if (r.unread) out.unread += 1
    if (r.broken) out.broken += 1
    if (r.dirty) {
      out.dirty += 1
      if (r.dirtyFiles != null) out.dirtyFiles = (out.dirtyFiles ?? 0) + r.dirtyFiles
    } else if (r.dirtyUnknown) out.dirtyUnknown += 1

    if (repo.hostId && !out.hosts.includes(repo.hostId)) out.hosts.push(repo.hostId)
    if (r.branch && !out.branches.includes(r.branch)) out.branches.push(r.branch)

    for (const s of repo.scan?.stack ?? repo.detectedStack ?? []) {
      const agent = AGENT_MARK[s]
      if (agent) {
        if (!out.agents.includes(agent)) out.agents.push(agent)
      } else if (!out.stack.includes(s)) {
        out.stack.push(s)
      }
    }

    for (const o of repo.scan?.credentialOverrides ?? []) {
      out.credentialOverrides.push({ repository: repo.name, path: o.path, setting: o.setting })
    }

    const scan = repo.scan
    if (scan?.headCommit && (!out.head?.at || (scan.headAt ?? '') > (out.head.at ?? ''))) {
      out.head = {
        at: scan.headAt,
        subject: scan.headSubject,
        sha: scan.headCommit,
        author: scan.headAuthor,
      }
    }
  }

  return out
}

/**
 * Whether a project matches what the operator typed.
 *
 * Deliberately wide: a name, a slug, a branch, a host name, a stack entry and
 * the path on disk are all things an operator has in their head when they come
 * looking for a project, and making them guess which field is searched is the
 * kind of small cruelty a console should not commit. `hostName` resolves ids to
 * the names the operator actually knows.
 */
export function matchesProject(
  project: Project,
  query: string,
  hostName: (id: string) => string | undefined,
): boolean {
  const q = query.trim().toLocaleLowerCase('tr')
  if (!q) return true

  const hay: string[] = [project.name, project.slug, project.description ?? '']
  for (const repo of project.repositories ?? []) {
    hay.push(repo.name, repo.path, repo.remote ?? '', hostName(repo.hostId) ?? '')
    hay.push(...(repo.scan?.stack ?? repo.detectedStack ?? []))
    if (repo.scan?.branch) hay.push(repo.scan.branch)
  }
  return hay.some((h) => h.toLocaleLowerCase('tr').includes(q))
}

/**
 * The states worth filtering a list of projects down to.
 *
 * Each one answers a question an operator asks out loud: what needs attention,
 * what is being worked on right now, what cannot be given to an agent yet.
 */
export type ProjectFilter = 'all' | 'attention' | 'running' | 'unconfigured'

/** Whether a project is in a state that wants looking at: a checkout Kimene
 *  cannot read, one nothing has ever scanned, or one whose own configuration
 *  overrides the credential a session would be routed to. A dirty tree is
 *  deliberately *not* here — uncommitted work is normal, and calling it a fault
 *  would make the filter useless on any machine somebody is working on.
 *
 *  The override belongs here and not merely on the detail panel: it is the one
 *  finding an operator cannot reach by looking at the thing it affects, because
 *  the session it diverts reports the account Kimene picked and runs on another.
 *  A signal with nowhere to go is decoration. */
export function needsAttention(reading: ProjectReading): boolean {
  return (
    reading.broken > 0 ||
    reading.unread > 0 ||
    reading.repositories === 0 ||
    reading.credentialOverrides.length > 0
  )
}

/** Whether an agent launched here would start with no project instructions at
 *  all. Not a fault — a new project is legitimately empty — but it is the
 *  reason a session there behaves like a stranger. */
export function unconfigured(reading: ProjectReading): boolean {
  return reading.repositories > 0 && reading.agents.length === 0
}

export function passesFilter(
  filter: ProjectFilter,
  reading: ProjectReading,
  liveSessions: number,
): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'attention':
      return needsAttention(reading)
    case 'running':
      return liveSessions > 0
    case 'unconfigured':
      return unconfigured(reading)
  }
}
